import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Pagination } from '@heroui/react';
import { countParam } from '@/pages/catalogImport/notices';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ImportHistoryPages } from './useImportHistory';

type ImportHistoryPaginationProps = {
    pagination: ImportHistoryPages;
};

/**
 * Under the table when the history runs past one page: which imports are
 * showing, then Previous and Next. No page links: a branch's history is a
 * few pages at most, read newest first.
 */
export const ImportHistoryPagination: React.FC<
    ImportHistoryPaginationProps
> = ({ pagination }) => {
    const { t } = useTranslation('pages');

    return (
        <Pagination
            className="w-full"
            size="sm"
            {...getNodeTestIdProp('branch-import-history-pagination')}
        >
            <Pagination.Summary
                {...getNodeTestIdProp('branch-import-history-page-summary')}
            >
                {t('catalogImport.branchImport.history.pagination.summary', {
                    from: countParam(pagination.from),
                    to: countParam(pagination.to),
                    total: countParam(pagination.totalCount),
                })}
            </Pagination.Summary>
            <Pagination.Content>
                <Pagination.Item>
                    <Pagination.Previous
                        aria-label={t(
                            'catalogImport.branchImport.history.pagination.previous',
                        )}
                        isDisabled={!pagination.canGoBack}
                        onPress={pagination.onPrevious}
                        {...getNodeTestIdProp(
                            'branch-import-history-page-previous',
                        )}
                    >
                        <Pagination.PreviousIcon />
                    </Pagination.Previous>
                </Pagination.Item>
                <Pagination.Item>
                    <Pagination.Next
                        aria-label={t(
                            'catalogImport.branchImport.history.pagination.next',
                        )}
                        isDisabled={!pagination.canGoOn}
                        onPress={pagination.onNext}
                        {...getNodeTestIdProp(
                            'branch-import-history-page-next',
                        )}
                    >
                        <Pagination.NextIcon />
                    </Pagination.Next>
                </Pagination.Item>
            </Pagination.Content>
        </Pagination>
    );
};
